import React, { useCallback } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuPosition } from 'ui-components/components/INTERNAL/dropdown/dropdown-menu-position';
import { noop } from 'ui-components/default-props/noop';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import style from './account-menu-dropdown.module.less';
import messages from './messages';
import LogoutService from '../../../authentication/logout/logout-service';
import { WalletBalanceInfo } from '../../../components/wallet-balance-info';
import { useModal } from '../../../hooks/use-modal';
import { useSellerWallets } from '../../../hooks/use-seller-wallets';
import { useUserHasPurchasingRole } from '../../../hooks/use-user-role';
import { AutoFundingModal } from '../../../pages/settings/payment-and-subscription/components/auto-fund-modal/auto-funding-modal';
import { useDeriveSubscriptionInfo } from '../../../pages/settings/payment-and-subscription/hooks/use-derive-subscription-info';
import useWalletSaveAutoFunding from '../../../pages/settings/providers/add-funds-modal/hooks/use-wallet-save-auto-funding';
import { PAGE_SIZE_50 } from '../../../services/pagination-service';
import { UserPermissionService } from '../../../services/user-permission-service';
import Global from '../../../store/global-selectors';
import { getOrderAlerts } from '../../../store/order-alerts/order-alerts.api';
import { sellerProvidersUpdated } from '../../../store/seller/seller.actions';
import { EventSourceStrings } from '../../../tracking/tracking-constants';
import { WalletDataContextProvider } from '../../../wallet/contexts/wallet-data-context-provider';
import { LanguageSelectionModal } from '../../modals/language-selection-modal';
import { OrderAlertsModal } from '../../modals/order-alerts-modal';
import { AccountIcon } from '../icons/account-icon';
import sharedStyles from '../shared-classes.module.less';

const AccountMenuDropdown = ({ isSystemAdmin = false }) => {
  const orderAlertCount = useSelector(Global.selectOrdersNavOrderAlertCount);
  const [orderAlerts, setOrderAlerts] = useAsyncSafeState([]);
  const alerts = useSelector((state) => {
    return orderAlerts.map((orderAlert) => {
      const salesOrderID =
        orderAlert.orderID &&
        Global.selectEntitiesSalesOrderIdsByFulfillmentPlanIds(state, {
          fulfillmentPlanIds: [orderAlert.orderID],
        })[0];
      return {
        ...orderAlert,
        salesOrderID,
      };
    });
  });
  const [isModalOpen, setIsModalOpen] = useAsyncSafeState(false);
  const { close: closeAutoFundModal, isOpen: autoFundModalIsOpen, open: openAutoFundModal } = useModal();
  const toggleLanguageSelectionModal = useCallback(() => setIsModalOpen(!isModalOpen), [isModalOpen, setIsModalOpen]);
  const getAlerts = useCallback(async () => {
    setOrderAlerts(await getOrderAlerts(1, PAGE_SIZE_50));
  }, [setOrderAlerts]);
  const clearAlerts = useCallback(() => {
    setOrderAlerts([]);
  }, [setOrderAlerts]);
  const logout = useCallback(async () => {
    await LogoutService.logout();
  }, []);

  const wallets = useSellerWallets();
  const wallet = wallets[0];
  const isSetupComplete = wallet?.isSetupComplete;
  const onSaveAutoFundConfig = useWalletSaveAutoFunding(wallet?.walletId);
  const sellerDefaultCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const sellerCurrencyCode = wallet?.balance?.code || sellerDefaultCurrencyCode;
  const dispatch = useDispatch();
  const planInfo = useSelector(Global.selectSellerSubscriptionPlanInfo);
  const { isTrialExpired } = planInfo;
  const { inactiveAccount } = useDeriveSubscriptionInfo(planInfo);
  const userRoles = useSelector(Global.selectUseruserRoles);
  const isManager = UserPermissionService.userHasAccountManagementRole(userRoles);
  const canAddFunds = useUserHasPurchasingRole();
  const showAutoFundOption = canAddFunds && isSetupComplete && !isTrialExpired && !inactiveAccount;
  const showWalletBalance = (canAddFunds || isManager) && isSetupComplete;

  const onAutoFundClick = useCallback(
    (e) => {
      e.stopPropagation();
      openAutoFundModal();
    },
    [openAutoFundModal],
  );

  const onSuccess = useCallback(() => {
    dispatch(sellerProvidersUpdated());
    closeAutoFundModal();
  }, [closeAutoFundModal, dispatch]);

  const hasAccountAlerts = orderAlertCount > 0;

  return (
    <>
      <DropdownMenu
        className={style['top-level']}
        dropdownMenuClassName={style['account-menu-container']}
        menuPosition={DropdownMenuPosition.BottomRight}
      >
        <DropdownMenuToggler
          className={classnames(sharedStyles['icon-wrapper'], style['account-menu-button'])}
          hideChevron
          tabIndex={-1}
          theme='unstyled'
        >
          <AccountIcon
            alertCount={orderAlertCount}
            className={style['account-icon']}
            decorationClassName={classnames({
              [style['alert-icon']]: hasAccountAlerts,
            })}
            showDecoration={hasAccountAlerts}
          />
        </DropdownMenuToggler>
        <WalletDataContextProvider>
          {
            // If user has Account Manager role they can SEE the wallet balance but not add funds to it
            wallets.map((w) => {
              return showWalletBalance ? (
                <WalletBalanceInfo key={w.walletId} trackingId={EventSourceStrings.MY_ACCOUNT_DROPDOWN} walletId={w.walletId} />
              ) : null;
            })
          }
        </WalletDataContextProvider>
        <div>
          {showAutoFundOption ? (
            <DropdownMenuItem onClick={(e) => onAutoFundClick(e)}>
              <FormattedMessage {...messages.manageFunds} />
            </DropdownMenuItem>
          ) : null}
          {orderAlertCount > 0 && (
            <DropdownMenuItem onClick={getAlerts}>
              <FormattedMessage {...messages.alerts} values={{ orderAlertCount }} />
            </DropdownMenuItem>
          )}
          <DropdownMenuItem>
            {({ className }) => (
              <NavLink className={className} to='/settings/myprofile'>
                <FormattedMessage {...messages.myProfile} />
              </NavLink>
            )}
          </DropdownMenuItem>
          {isSystemAdmin ? (
            <DropdownMenuItem href='/admin' onClick={noop} target='_self'>
              <FormattedMessage {...messages.admin} />
            </DropdownMenuItem>
          ) : null}
          {isSystemAdmin ? (
            <DropdownMenuItem href='https://mgmt.shipstation.com/#/admin/sellers' onClick={noop}>
              <FormattedMessage {...messages.management} />
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onClick={toggleLanguageSelectionModal}>
            <FormattedMessage {...messages.language} />
          </DropdownMenuItem>
          <DropdownMenuItem onClick={logout} testId='topnav-logout-button'>
            <FormattedMessage {...messages.logout} />
          </DropdownMenuItem>
        </div>
      </DropdownMenu>
      {isModalOpen ? <LanguageSelectionModal onClose={toggleLanguageSelectionModal} /> : null}
      {alerts.length > 0 && <OrderAlertsModal alerts={alerts} onClose={clearAlerts} />}
      {autoFundModalIsOpen ? (
        <AutoFundingModal
          autoFundConfig={wallet.autoFundConfig}
          onClose={closeAutoFundModal}
          onSaveAutoFundConfig={onSaveAutoFundConfig}
          onSuccess={onSuccess}
          sellerCurrencyCode={sellerCurrencyCode}
        />
      ) : null}
    </>
  );
};

AccountMenuDropdown.propTypes = {
  isSystemAdmin: PropTypes.bool,
};

export default AccountMenuDropdown;
